import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/*
 * © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { RatingContainer, RatingIcon } from './ratingStyles';
import { fillIcon as embersFillIcon, emptyIcon as embersEmptyIcon, hoverIcon as embersHoverIcon, disabledFillIcon as embersDisabledIcon, disabledEmptyIcon as embersDisabledEmptyIcon } from './ratingIcons';
import classnames from 'classnames';
import { onKeyPress } from '../utils/wcagUtils';
import { useIntl } from 'react-intl';
import { useId } from '../utils/useId';
export var EmbersRating = function EmbersRating(_ref) {
  var value = _ref.value,
    defaultValue = _ref.defaultValue,
    icons = _ref.icons,
    onChange = _ref.onChange,
    getCollectionLabel = _ref.getCollectionLabel,
    getIconLabel = _ref.getIconLabel,
    collectionCount = _ref.collectionCount,
    label = _ref.label,
    _ref$labelAlign = _ref.labelAlign,
    labelAlign = _ref$labelAlign === void 0 ? 'right' : _ref$labelAlign,
    className = _ref.className,
    readOnly = _ref.readOnly,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 5 : _ref$max,
    disabled = _ref.disabled,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _id = _ref.id;
  var _ref2 = icons || {},
    icon = _ref2.icon,
    emptyIcon = _ref2.emptyIcon,
    hoverIcon = _ref2.hoverIcon,
    disabledIcon = _ref2.disabledIcon,
    disabledEmptyIcon = _ref2.disabledEmptyIcon;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showHover = _useState2[0],
    setShowHover = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    hasHover = _useState4[0],
    setHasHover = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    hasFocus = _useState6[0],
    setHasFocus = _useState6[1];
  var _useState7 = useState(defaultValue || value || 0),
    _useState8 = _slicedToArray(_useState7, 2),
    rating = _useState8[0],
    setRating = _useState8[1];
  var id = useId(_id);
  var intl = useIntl();

  // prevents half star ratings on editable ratings
  useEffect(function () {
    !readOnly && setRating(Math.floor(rating));
  }, [readOnly]);
  useEffect(function () {
    value && setRating(value);
  }, [value]);
  var handleRatingChange = function handleRatingChange(event, value) {
    onChange && onChange(event, value);
    setShowHover(false);
    setRating(value);
  };
  var handleKeyDown = function handleKeyDown(e) {
    e.key !== 'Tab' && e.preventDefault();
    switch (e.key) {
      case 'Tab':
      case 'Shift-Tab':
        setHasFocus(0);
        break;
      case 'Right': // IE/Edge specific value
      case 'ArrowRight':
      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        if (rating) {
          if (rating < 5) {
            rating !== hasFocus ? handleRatingChange(e, hasFocus) : handleRatingChange(e, rating + 1);
            setHasFocus(Math.min(hasFocus + 1, 5));
          }
        } else {
          handleRatingChange(e, 1);
        }
        break;
      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
      case 'Left': // IE/Edge specific value
      case 'ArrowLeft':
        if (rating > 1) {
          handleRatingChange(e, rating - 1);
          setHasFocus(Math.max(hasFocus - 1, 1));
        } else {
          rating && handleRatingChange(e, 0);
        }
        break;
      default:
        break;
    }
  };
  var handleIconLabel = function handleIconLabel(count, rating) {
    var ratingString = rating && count !== 0 ? "".concat(intl.formatMessage({
      id: 'embers.starRating.rating',
      defaultMessage: 'rating'
    }), " ") : '';
    var starsString = rating && count === 0 ? intl.formatMessage({
      id: 'embers.starRating.noRating',
      defaultMessage: 'no rating'
    }) : intl.formatMessage({
      id: 'embers.starRating.stars',
      defaultMessage: '{count, plural, =1 {# star} other {# stars}}'
    }, {
      count: count
    });
    return getIconLabel ? getIconLabel(count) : "".concat(ratingString).concat(starsString);
  };
  var handleCountLabel = function handleCountLabel(count) {
    return getCollectionLabel ? getCollectionLabel(count) : intl.formatMessage({
      id: 'embers.starRating.users',
      defaultMessage: '{count} {count, plural, =1 {user} other {users}}'
    }, {
      count: count
    });
  };
  var isFilled = function isFilled(ratingIcon) {
    return ratingIcon === (icon || embersFillIcon) || icon === (disabledIcon || embersDisabledIcon);
  };
  var renderStar = function renderStar(v) {
    var fill = icon || embersFillIcon;
    var empty = emptyIcon || embersEmptyIcon;
    var hover = hoverIcon || embersHoverIcon;
    var disabledFill = disabledIcon || embersDisabledIcon;
    var disabledEmpty = disabledEmptyIcon || embersDisabledEmptyIcon;
    var showHalf = rating < v && rating > v - 1;
    var ratingIcon = v <= rating ? disabled ? disabledFill : fill : showHover && v <= hasHover ? hover : disabled ? disabledEmpty : empty;
    return /*#__PURE__*/React.createElement(RatingIcon, {
      id: !readOnly && !disabled ? "rating-".concat(id, "-star-").concat(v) : '',
      key: v,
      role: "radio",
      "aria-checked": rating >= v
      // aria-label={handleIconLabel(!rating ? 0 : v)}
      ,
      "aria-label": handleIconLabel(v),
      className: classnames('EmbersRating-icon', v === hasFocus ? 'EmbersRating-hasFocus' : '', {
        'EmbersRating-half': showHalf
      }),
      onClick: function onClick(e) {
        if (readOnly || disabled) return;
        setHasFocus(0);
        if (rating) {
          handleRatingChange(e, 0);
          setShowHover(true);
        } else {
          handleRatingChange(e, v);
        }
      },
      onMouseEnter: function onMouseEnter() {
        return setHasHover(v);
      },
      onMouseLeave: function onMouseLeave() {
        return setHasHover(0);
      }
    }, showHalf && /*#__PURE__*/React.createElement("span", {
      className: "EmbersRating-star EmbersRating-starFill"
    }, disabled ? disabledFill : fill), /*#__PURE__*/React.createElement("span", {
      className: classnames('EmbersRating-star', {
        'EmbersRating-starFill': isFilled(ratingIcon),
        'EmbersRating-starEmpty': ratingIcon === empty || icon === disabledEmpty,
        'EmbersRating-starHover': ratingIcon === hover
      })
    }, ratingIcon));
  };
  return /*#__PURE__*/React.createElement(RatingContainer, {
    className: classnames('EmbersRating-root', "EmbersRating-".concat(size), {
      'EmbersRating-readOnly': readOnly,
      'EmbersRating-disabled': disabled
    }, className)
    // because of no role here (or if set role = none) then screen read through stops
    // todo: need to figure out if there is a way around it or if just a side affect of VoiceOver
    ,
    role: readOnly ? 'img' : void 0
    // double aria-label here and labelledby for selectable radio group is unusual
    // but was the only way to get readonly rating to read out and not stop
    // not sure if all components with an outer wrapper/container need to be rewritten or if just part of screen reader
    ,
    "aria-label": label ? collectionCount ? "".concat(label, " ").concat(handleCountLabel(collectionCount), " ").concat(handleIconLabel(rating, true)) : "".concat(label, " ").concat(handleIconLabel(rating, true)) : collectionCount ? "".concat(handleCountLabel(collectionCount), " ").concat(handleIconLabel(rating, true)) : "".concat(handleIconLabel(rating, true))
  }, /*#__PURE__*/React.createElement("div", {
    id: "star-rating-wrapper-".concat(id),
    className: "EmbersRating-Wrapper",
    style: {
      display: 'flex',
      flexDirection: 'row'
    },
    role: disabled ? 'none' : 'radiogroup',
    "aria-readonly": readOnly,
    "aria-labelledby": !readOnly ? "star-rating-label-".concat(id, " star-rating-count-").concat(id) || "star-rating-label-".concat(id) || "star-rating-count-".concat(id) || void 0 : void 0
    // radiogroup needs to be parent of radio options along with activedescendant
    ,
    "aria-activedescendant": !readOnly && !disabled ? rating ? "rating-".concat(id, "-star-").concat(rating) : "rating-".concat(id, "-star-1") : void 0,
    tabIndex: readOnly || disabled ? -1 : 0,
    onKeyUp: function onKeyUp(e) {
      return onKeyPress(e, ['Tab', 'Shift-Tab'], function () {
        return setHasFocus(rating || 1);
      });
    },
    onKeyDown: handleKeyDown,
    onMouseEnter: function onMouseEnter() {
      return !disabled && !readOnly && !rating && setShowHover(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setShowHover(false);
    },
    onBlur: function onBlur() {
      return setHasFocus(0);
    }
  }, Array.from({
    length: max
  }, function (v, i) {
    return i + 1;
  }).map(function (value) {
    return renderStar(value);
  })), collectionCount !== undefined && /*#__PURE__*/React.createElement("span", {
    id: "star-rating-count-".concat(id),
    className: "EmberRating-label count-label",
    "aria-label": handleCountLabel(collectionCount),
    "aria-hidden": true
  }, "( ", collectionCount, " )"), label && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    id: "star-rating-label-".concat(id),
    className: "EmberRating-label label-".concat(labelAlign)
  }, label));
};